// Strictly-presentational box-in-box detail shell shared by all four
// tracking-type read-only detail views (deliverable / milestone /
// insurance / meeting). Owns no per-type semantics: callers supply a
// title, an optional badge cluster, an optional description, a list of
// label/value fields (rendered as a <dl> grid), an optional action
// cluster, and arbitrary children for the type-specific extra sections
// (evidence links, notes, lifecycle timelines, callouts, etc.).
//
// The white card surface this renders provides the inner half of the
// box-in-box treatment — the outer neutral band + brand connector lives
// on TrackingTable's expanded-row cell.

import type { ReactNode } from "react";

export interface TrackingDetailField {
  label: string;
  value: ReactNode;
  /** Optional per-stage free-text comment (e.g. submittedComment) rendered
   *  beneath the value as an italic caption. Omitted rows render no comment
   *  element at all. */
  comment?: string | null;
  /** data-testid for the rendered comment element, so per-type tests can
   *  target the specific lifecycle comment. */
  commentTestId?: string;
}

interface TrackingDetailPanelProps {
  /** Heading content. ReactNode so callers can prefix a mono number etc. */
  title: ReactNode;
  /** Status / RAG pills cluster, composed by the caller. */
  badges?: ReactNode;
  /** Optional description paragraph below the header. Empty string renders
   *  nothing. */
  description?: string;
  /** Label/value pairs rendered as a two-column <dl> grid. */
  fields: TrackingDetailField[];
  /** Header right-side action cluster (Edit / Re-open / Mark held …). */
  actions?: ReactNode;
  /** Type-specific extra sections rendered below the field grid. */
  children?: ReactNode;
  /** Accessible label for the section. Falls back to "Details for {title}"
   *  when the title is a plain string. */
  ariaLabel?: string;
  /** data-testid for the outer section — each type passes its existing id
   *  so downstream tests keep targeting the same node. */
  testId?: string;
}

function Field({ label, value, comment, commentTestId }: TrackingDetailField) {
  return (
    <div>
      <dt className="text-caption text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-1 text-body text-slate-900 dark:text-slate-100">
        {value}
      </dd>
      {comment && (
        <dd
          data-testid={commentTestId}
          className="mt-1 text-caption italic text-slate-600 whitespace-pre-wrap dark:text-slate-400"
        >
          {comment}
        </dd>
      )}
    </div>
  );
}

export default function TrackingDetailPanel({
  title,
  badges,
  description,
  fields,
  actions,
  children,
  ariaLabel,
  testId,
}: TrackingDetailPanelProps) {
  const resolvedLabel =
    ariaLabel ??
    (typeof title === "string" ? `Details for ${title}` : undefined);

  return (
    <section
      data-testid={testId}
      aria-label={resolvedLabel}
      className="space-y-5 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-h3 text-slate-900 dark:text-slate-100">
            {title}
          </h3>
          {badges && (
            <div className="flex flex-wrap items-center gap-2">{badges}</div>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2">{actions}</div>
        )}
      </header>

      {description && (
        <p className="text-body text-slate-700 dark:text-slate-300">
          {description}
        </p>
      )}

      {fields.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
          {fields.map((field) => (
            <Field key={field.label} {...field} />
          ))}
        </dl>
      )}

      {children}
    </section>
  );
}
